import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import type {AnnotateFixedPlateWellInput, FixedPlateTransferInputWell} from 'samples/GridDAO';
import {
  MeasurementLayer,
  MeasurementLayerValue,
  SupportedFieldTypeForAnnotation,
  WellPosition,
} from 'samples/plate-maps/PlateMapConstants';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface PlateDesignVersionRef {
  plateDesignApiId: string;
  plateDesignVersionApiId: string;
}

/**
 * A record of plate annotations (well role, container schema values) and transfer of reagents
 * made to either an existing plate or upon creation of a new plate.
 *
 * Matches JSON schema defined in benchling/notebook/note_linked_plate_record/models/plate_record_serializer.py
 */
export interface PlateRecord {
  id: string;
  plateApiId: string;
  creatorApiId: string;
  createdAt: string;
  transferType: PlateTransferType;
  wellTransferRecords: Array<PlateWellTransferRecord>;
  frozenPlateMetadata?: FrozenPlateMetadata | null;
  plateDesignVersionRef?: PlateDesignVersionRef | null;
  schemaFieldValues?: Array<SupportedFrozenSchemaFieldValue> | null;
}

// Matches TransferType types in benchling/benchling_domain_graph/types/dataclass_types.py
export enum PlateTransferType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

export type BaseReadonlyPlateWellTransfer<
  TCoordinatesKey extends string,
  TTransferKey extends string,
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
> = {
  [key in TCoordinatesKey]: WellPosition;
} & {
  [key in TTransferKey]: ContainerTransferRecord<TMeasurement>;
} & {
  role: ExperimentalRole | null;
  schemaFieldValues: Array<SupportedFrozenSchemaFieldValue>;
  dataParameterValues?: Array<SupportedFrozenDataParameterValue>;
  measurementLayerValues?: Array<MeasurementLayerValue<TMeasurement>> | null;
};

export type PlateWellTransferRecord = BaseReadonlyPlateWellTransfer<
  'position',
  'transferRecord',
  PlateRecordMeasurement
>;
export interface FrozenSchemaFieldValue<T> {
  schemaFieldValue: SchemaFieldValue<T>;
  frozenMetadata: {
    /** Display name of the schema field. */
    fieldName: string;
    fieldType: FieldType;
    isMulti?: boolean | null;
  };
  frozenNames?: Array<string>;
}

export interface FrozenDataParameterValue<T> {
  dataParameterVersionId: string;
  dataParameterName?: string | null;
  frozenSchemaFieldValue: FrozenSchemaFieldValue<T>;
}

export interface SchemaFieldValue<T> {
  fieldDefinitionId: string;
  textValue: string | null;
  value: T;
  url?: string | null;
}

export type SupportedFrozenSchemaFieldValue = FrozenSchemaFieldValue<
  SchemaFieldValueTypeByFieldType[SupportedFieldTypeForAnnotation]
>;

export type SupportedFrozenDataParameterValue = FrozenDataParameterValue<
  SchemaFieldValueTypeByFieldType[SupportedFieldTypeForAnnotation]
>;
interface SchemaFieldValueTypeByFieldType {
  [FieldType.TEXT]: string;
  [FieldType.SELECTOR]: Array<string>;
  [FieldType.INTEGER]: number;
  [FieldType.FLOAT]: number;
}

export interface FrozenSchemaMetadata {
  schemaApiId: string;
  name: string;
  iconId: BenchlingIconName;
}

export interface FrozenEntityChipData {
  name: string;
  iconId: BenchlingIconName | null;
  /**
   * Whether the entity is _currently_ inaccessible (permissions-wise) to the user.
   */
  isInaccessible: boolean;
}

export interface ContainerTransferRecord<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement> {
  sources: Array<ContainerTransferRecordSource<TMeasurement>>;
  contents: Array<ContainerTransferRecordContainerContent<TMeasurement>>;
  finalQuantity: TMeasurement | null;
}

export interface FrozenTestOrderInfo {
  testOrderId: string;
}

export interface ContainerTransferRecordSourceContainable<
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
> {
  entityApiId: string;
  entityName?: string | null;
  concentration: TMeasurement | null;
  finalConcentration: TMeasurement | null;
  transferQuantity: TMeasurement | null;
  frozenSchemaMetadata: FrozenSchemaMetadata | null;
  materialInputParameterVersionId?: string | null;
  testOrders?: Array<FrozenTestOrderInfo> | null;
}

export interface ContainerTransferRecordSourceContainer<
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
> {
  containerApiId: string;
  containerName?: string | null;
  contents: Array<ContainerTransferRecordContainerContent<TMeasurement>>;
  transferQuantity: TMeasurement | null;
  frozenSchemaMetadata: FrozenSchemaMetadata | null;
}

export type ContainerTransferRecordSource<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement> =
    | ContainerTransferRecordSourceContainable<TMeasurement>
    | ContainerTransferRecordSourceContainer<TMeasurement>;

export interface ContainerTransferRecordContainerContent<
  TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement,
> {
  content: ContainerContent<TMeasurement>;
  finalConcentration: TMeasurement | null;
  materialInputParameterVersionId?: string | null;
  testOrders?: Array<FrozenTestOrderInfo> | null;
}

interface ContainerContent<TMeasurement extends PlateRecordMeasurement | PlateMapMeasurement> {
  entityApiId: string;
  entityName?: string | null;
  concentration: TMeasurement | null;
  frozenSchemaMetadata: FrozenSchemaMetadata | null;
}

export function isPlateMapMeasurement(
  measurement: PlateRecordMeasurement | PlateMapMeasurement
): measurement is PlateMapMeasurement {
  return measurement.hasOwnProperty('value');
}

// This distinction will no longer be necessary once we share the plate map type for both records and designs.
// For now, records are tied to their db representation, so we cannot easily change it.
export interface PlateRecordMeasurement {
  siValue: number;
  displayUnit: TransferRecordMeasurementUnit;
}

export interface PlateMapMeasurement {
  value: number;
  displayUnit: TransferRecordMeasurementUnit;
}

export interface TransferRecordMeasurementUnit {
  unitApiId: string;
  unitVersionNumber: number;
}

interface FrozenPlateMetadata {
  containerSchemaId: string;
  width: number;
  height: number;
  plateType: PlateSchemaType;
}

export type PersistedPlateMapPosition = BaseReadonlyPlateWellTransfer<
  'coordinates',
  'transfer',
  PlateMapMeasurement
>;
/**
 * An API-compatible representation of a plate map.
 *
 * Keep in-sync with benchling/inventory/plate_map/core/plate_map_types.py:PlateMap
 */
export interface PersistedPlateMap {
  plateSchemaApiId: string;
  /**
   * Every well in the plate must be represented in this array.
   */
  positions: Array<PersistedPlateMapPosition>;
  frozenPlateMetadata?: FrozenPlateMetadata | null;
  measurementLayers?: Array<MeasurementLayer> | null;
}

// TODO(BNCH-100908): Share the same types once records use PlateMap*Input types.
export type PlateMapTransferInput = Omit<FixedPlateTransferInputWell, 'position'> | null;
export type PlateMapAnnotateInput = Omit<AnnotateFixedPlateWellInput, 'position'> | null;

export interface PlateMapPositionInput {
  coordinates: WellPosition;
  transfer_input: PlateMapTransferInput;
  annotate_input: PlateMapAnnotateInput;
}

export interface MeasurementLayerInput {
  layer_id: string;
  name: string;
}

/**
 *  Note: Our backend handlers operate with dataclasses with snake_case.
 *  To simplify converting API representations to dataclasses, Write APIs
 *  use snake_case, while Read APIs will use camelCase.
 */
export interface PlateMapInput {
  plate_schema_api_id: string;
  /**
   * Every well in the plate must be represented in this array.
   */
  positions: Array<PlateMapPositionInput>;
  measurement_layers?: Array<MeasurementLayerInput> | null;
}
